/**
 * Renders the NAME behind a stored record id.
 *
 * WHY THIS EXISTS. Several payloads carry only a foreign key —
 * `project.projectManagerEmployeeId`, `client.accountManagerEmployeeId` — and the screens
 * were rendering the key. A manager's name slot read
 * `emp_01KZ7XJAHZBRK0ZCDZPRMX13XH`, which is not information; it is the absence of
 * information formatted to look like information. Worse, it was wrapped in a link, so the
 * only way to find out who it was was to click through.
 *
 * The lookup is one cached `/platform/entity-refs/:type` request shared by every instance
 * on the page, so a table of forty rows resolves forty names with one round trip.
 *
 * When the id cannot be resolved the ID IS STILL SHOWN, in a muted tone, with a title
 * explaining why — because "unknown" would hide the fact that a record points somewhere
 * broken, and an operator chasing a dangling reference needs the id.
 */
import { useEntityRefs } from '@frontend/api/hooks/entityRefs';
import { cn } from '@frontend/lib/cn';

export function EntityRefName({
  entityType,
  id,
  fallback = 'Not assigned',
  className,
}: {
  entityType: string;
  id: string | null | undefined;
  /** Shown when the id itself is absent — a genuinely empty field, not a broken one. */
  fallback?: string;
  className?: string;
}): JSX.Element {
  const query = useEntityRefs(entityType);

  if (id === null || id === undefined || id === '') {
    return <span className="text-content-tertiary">{fallback}</span>;
  }

  const match = query.data?.items.find((item) => item.id === id);
  if (match !== undefined) return <span className={className}>{match.label}</span>;

  if (query.isLoading) return <span className="text-content-tertiary">Loading…</span>;

  return (
    <span
      className={cn('text-content-tertiary', className)}
      title={
        `This record points at ${entityType.replace(/_/g, ' ')} "${id}", which is not in the current list. ` +
        'It may have been deactivated, or the reference may be stale.'
      }
    >
      <span className="code-ref">{id}</span>
    </span>
  );
}
